import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

export interface ItemCategory {
  id: number;
  categoryName: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ItemCategoryCreateRequest {
  categoryName: string;
  description?: string;
  isActive: boolean;
}

export interface ItemCategoryUpdateRequest extends ItemCategoryCreateRequest {
  id: number;
}

class ItemCategoryApiService extends BaseApiService {
  // Get all item categories
  async getItemCategories(): Promise<ItemCategory[]> {
    try {
      const response = await this.makeRequest<ApiResponse<ItemCategory[]>>(API_ENDPOINTS.CATEGORIES.LIST);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch item categories');
    } catch (error) {
      toast.error('Failed to fetch item categories');
      throw error;
    }
  }

  // Get item category by ID
  async getItemCategoryById(id: string): Promise<ItemCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<ItemCategory>>(API_ENDPOINTS.CATEGORIES.DETAIL(id));
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch item category details');
    } catch (error) {
      toast.error('Failed to fetch item category details');
      throw error;
    }
  }

  // Create new item category
  async createItemCategory(itemCategoryData: ItemCategoryCreateRequest): Promise<ItemCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<ItemCategory>>(API_ENDPOINTS.CATEGORIES.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(itemCategoryData),
      });
      if (response.success) {
        toast.success(response.message || 'Item category created successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create item category');
    } catch (error) {
      toast.error('Failed to create item category');
      throw error;
    }
  }

  // Update item category
  async updateItemCategory(id: string, itemCategoryData: ItemCategoryUpdateRequest): Promise<ItemCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<ItemCategory>>(API_ENDPOINTS.CATEGORIES.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(itemCategoryData),
      });
      if (response.success) {
        toast.success(response.message || 'Item category updated successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to update item category');
    } catch (error) {
      toast.error('Failed to update item category');
      throw error;
    }
  }

  // Delete item category
  async deleteItemCategory(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<unknown>>(API_ENDPOINTS.CATEGORIES.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      if (response?.success) {
        toast.success(response.message || 'Item category deleted successfully!');
      } else {
        throw new Error(response?.message || 'Failed to delete item category');
      }
    } catch (error) {
      toast.error('Failed to delete item category');
      throw error;
    }
  }
}

export const itemCategoryApiService = new ItemCategoryApiService();
